import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, ArrowLeft, ShoppingBag, Lock, ShieldCheck, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Cart = () => {
    const { cartItems, removeFromCart, getTotal, clearCart } = useCart();
    const [email, setEmail] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const total = getTotal();

    const handleCheckout = async () => {
        if (!email) {
            toast.error('Please enter your email address');
            return;
        }
        
        if (!email.includes('@')) {
            toast.error('Please enter a valid email address');
            return;
        }

        setIsProcessing(true);
        
        try {
            const response = await axios.post(`${API}/stripe/create-checkout`, {
                recording_ids: cartItems.map(item => item.id),
                email: email,
                origin_url: window.location.origin
            });
            
            // Redirect to Stripe Checkout
            if (response.data.checkout_url) {
                window.location.href = response.data.checkout_url;
            } else {
                throw new Error('No checkout URL received');
            }
        } catch (error) {
            console.error('Failed to create checkout:', error);
            toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
            setIsProcessing(false);
        }
    };

    if (cartItems.length === 0) {
        return (
            <div className="min-h-screen bg-nature-50 pt-24">
                <div className="max-w-4xl mx-auto px-6 py-16">
                    <div className="text-center">
                        <div className="w-24 h-24 bg-nature-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <ShoppingBag className="w-12 h-12 text-nature-800/30" />
                        </div>
                        <h1 className="font-heading text-3xl text-nature-900 mb-4">Your Cart is Empty</h1>
                        <p className="text-nature-800/70 font-body mb-8">
                            Explore our collection of transformational hypnosis recordings.
                        </p>
                        <Link to="/recordings">
                            <Button className="rounded-full bg-copper-500 hover:bg-copper-600">
                                Browse Recordings
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-nature-50 pt-24">
            <div className="max-w-6xl mx-auto px-6 py-12">
                <Link 
                    to="/recordings" 
                    className="inline-flex items-center gap-2 text-nature-800/70 hover:text-copper-500 mb-8 font-body transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Continue Shopping
                </Link>

                <h1 className="font-heading text-4xl font-light text-nature-900 mb-8">
                    Your <span className="italic">Cart</span>
                </h1>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Cart Items */}
                    <div className="lg:col-span-2 space-y-4">
                        {cartItems.map((item) => (
                            <div 
                                key={item.id} 
                                className="bg-white rounded-xl p-4 shadow-soft flex gap-4"
                                data-testid={`cart-item-${item.id}`}
                            >
                                <img 
                                    src={item.cover_image} 
                                    alt={item.title}
                                    className="w-20 h-20 rounded-lg object-cover"
                                />
                                <div className="flex-1">
                                    <h3 className="font-heading text-lg text-nature-900">{item.title}</h3>
                                    <p className="text-nature-800/60 font-body text-sm">{item.category}</p>
                                    <p className="text-copper-500 font-heading font-semibold mt-1">
                                        €{item.price.toFixed(2)}
                                    </p>
                                </div>
                                <button 
                                    onClick={() => removeFromCart(item.id)}
                                    className="text-nature-800/40 hover:text-red-500 transition-colors p-2"
                                    data-testid={`remove-${item.id}`}
                                >
                                    <Trash2 className="w-5 h-5" />
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-xl p-6 shadow-soft sticky top-24">
                            <h2 className="font-heading text-xl text-nature-900 mb-6">Order Summary</h2>
                            
                            <div className="space-y-3 mb-6">
                                <div className="flex justify-between font-body text-nature-800/70">
                                    <span>Subtotal ({cartItems.length} items)</span>
                                    <span>€{total.toFixed(2)}</span>
                                </div>
                                <div className="border-t border-nature-100 pt-3 flex justify-between font-heading text-lg text-nature-900">
                                    <span>Total</span>
                                    <span className="text-copper-500">€{total.toFixed(2)}</span>
                                </div>
                            </div>

                            {/* Email Input */}
                            <div className="mb-6">
                                <Label htmlFor="email" className="text-nature-800 font-body text-sm mb-2 block">
                                    Email for download links
                                </Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="your@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="rounded-lg border-nature-200 focus:border-copper-500"
                                    data-testid="checkout-email"
                                    required
                                />
                            </div>

                            {/* Checkout Button */}
                            <Button
                                onClick={handleCheckout}
                                disabled={!email || isProcessing}
                                className="w-full rounded-full bg-copper-500 hover:bg-copper-600 h-12 text-base"
                                data-testid="checkout-button"
                            >
                                {isProcessing ? (
                                    <span className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Processing...
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        <CreditCard className="w-5 h-5" />
                                        Checkout — €{total.toFixed(2)}
                                    </span>
                                )}
                            </Button>
                            
                            <div className="flex items-center justify-center gap-2 mt-4 text-nature-800/50 text-sm font-body">
                                <Lock className="w-4 h-4" />
                                Secure checkout with Stripe
                            </div>
                            
                            {/* Money-Back Guarantee */}
                            <div className="mt-4 p-3 bg-nature-50 rounded-xl border border-nature-200">
                                <div className="flex items-center gap-2 text-nature-900">
                                    <ShieldCheck className="w-5 h-5 text-copper-500 flex-shrink-0" />
                                    <div>
                                        <span className="font-heading font-semibold text-sm">30-Day Money-Back Guarantee</span>
                                        <p className="text-nature-800/60 font-body text-xs">
                                            Not satisfied? Full refund, no questions asked.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Cart;
